/**
 * 外设调试 API — 设备管理分组各测试页共用的 jinglunGo (127.0.0.1:8765) 端点封装.
 *
 * 与 scaleClient.js / jinglunReader.js / go2rtc.js 分工:
 *   - 地磅/ONVIF → scaleClient.js;  读卡/身份证/NFC → jinglunReader.js;  go2rtc 状态/同步 → go2rtc.js
 *   - 本文件只补驱动管理 (drivers) + go2rtc 启停/ffmpeg + 通用健康检查 这些没被覆盖的端点.
 * 全部走 fetch (loopback 混合内容豁免), 返回 {ok, data} 拆包, 失败 throw Error(message).
 */
import { PERIPHERAL_BASE } from './go2rtc'
import { peripheralAccessHint } from './peripheralHealth'

async function request(method, path, body, timeoutMs = 15000) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => { try { controller.abort() } catch (e) { /* ignore */ } }, timeoutMs) : null
  try {
    const resp = await fetch(PERIPHERAL_BASE + path, {
      method,
      ...(body != null ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {})
    })
    const json = await resp.json().catch(() => null)
    if (!json || json.ok !== true) {
      const msg = (json && json.error && json.error.message) || (path + ' 请求失败')
      throw new Error(msg)
    }
    return json.data
  } catch (e) {
    if (e && e.name === 'AbortError') throw new Error('请求超时（' + path + '）')
    // [2026-09-09] 网络层失败区分不出"服务没启动"和"浏览器拦了本地网络权限", 提示里两者都覆盖
    if (e instanceof TypeError) throw new Error(peripheralAccessHint())
    throw e
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const PeripheralDebugApi = {
  /** 服务健康: { backend, readerType, sdkLoaded, sdkLoadError, opened, currentPort } */
  health() {
    return request('GET', '/api/health')
  },

  /** 读卡器设备枚举: { devices: [...] } */
  listDevices() {
    return request('GET', '/api/devices')
  },

  // ===== 驱动管理 =====

  /**
   * 驱动安装状态. 过滤 python_* 项（Go 版 jinglunGo 纯 Go 编译不依赖 Python，
   * drivers.go 仍硬编码检测会在 results 里报"缺失"，对实际功能无影响，滤掉防误导）
   */
  async checkDrivers() {
    const d = await request('GET', '/api/drivers/check')
    if (d && Array.isArray(d.results)) {
      d.results = d.results.filter(r => !String(r.id || '').startsWith('python_'))
      if (d.summary) {
        d.summary.total = d.results.length
        d.summary.missing = d.results.filter(r => !r.installed).length
        d.summary.installed = d.results.filter(r => r.installed).length
      }
    }
    return d
  },

  /** 安装包目录清单: { dir, dirExists, drivers: [{id, available, file, name}] } */
  listInstallers() {
    return request('GET', '/api/drivers/installers')
  },

  /** 安装单个驱动: { success, message, output } */
  installDriver(id) {
    return request('POST', '/api/drivers/install', { id }, 600000) // [在线安装] 缺包时先从发布通道下载, 大包(go2rtc ~62MB)需长超时
  },

  /** 卸载单个驱动 */
  uninstallDriver(id) {
    return request('POST', '/api/drivers/uninstall', { id }, 120000)
  },

  /** 一键安装全部缺失驱动（顺序静默装） */
  installAllDrivers() {
    return request('POST', '/api/drivers/install_all', {}, 600000)
  },

  // ===== go2rtc 启停/ffmpeg（状态与配置在 go2rtc.js） =====

  startGo2rtc() {
    return request('POST', '/api/go2rtc/start', {}, 600000) // [在线安装] go2rtc 缺失时启动会先在线拉组件包
  },

  stopGo2rtc() {
    return request('POST', '/api/go2rtc/stop', {}, 15000)
  },

  /** ffmpeg 可用性: { working, exists, version, message, path } */
  checkFfmpeg() {
    return request('GET', '/api/go2rtc/check-ffmpeg')
  },

  /**
   * RTSP 连通诊断（完整参数版，支持无中心模式通道级覆盖）.
   * @param {Object} body { ip, rtspPort, username, password, brand, channelId, streamType,
   *                        channelIp, channelUsername, channelPassword, channelRtspPort, rtspUrl }
   * @returns { rtspPortReachable, rtspResponded, message, probeUrl }
   */
  probeRtsp(body) {
    return request('POST', '/api/go2rtc/probe', body, 20000)
  },

  /**
   * RTSP 地址自动识别：按品牌候选列表逐个做真实 DESCRIBE 验证（带认证），
   * 命中即返回可用的取流地址。诊断连通只测 OPTIONS，测不出路径/账密问题，本接口才能。
   * @param {Object} body 同 probeRtsp + 可选 rtspUrl（ONVIF/手填地址优先验证）
   * @returns { ok, url, label, codec, warning?, tried: [{label,url,ok,code,err}] }
   */
  autoDetectRtsp(body) {
    // 候选逐个验证，最坏 ~30 秒（全品牌 + 每个超时 3 秒）
    return request('POST', '/api/go2rtc/auto-detect', body, 60000)
  },

  // ===== 日志导出 =====

  /**
   * [2026-10-05] 导出外设服务日志到桌面 — 与托盘「导出日志」同一出口.
   * 打包 logs 目录全部 *.log + 电脑现状报告 system-info.txt 成 zip 放到桌面,
   * 完成后外设服务会自动打开文件夹定位 zip.
   * @returns { zipName, zipPath } — zipName 用于成功提示回显
   */
  exportLogs() {
    // 同步打包 ≈1s（含 400ms CPU 采样），30s 上限防旧机器驱动检测慢导致误报超时
    return request('POST', '/api/logs/export', {}, 30000)
  }
}

export default PeripheralDebugApi
